import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../ui';
import { STORAGE_KEYS } from '../../constants/config';
import { formatTime } from '../../utils';

export const SuccessRecordsModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      const savedRecords = JSON.parse(localStorage.getItem(STORAGE_KEYS.SUCCESS_RECORDS) || '[]');
      setRecords(savedRecords.reverse());
    }
  }, [isOpen]);

  useEffect(() => {
    let filtered = records;
    
    if (filter !== 'all') {
      filtered = filtered.filter(record => record.type === filter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(record => 
        record.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredRecords(filtered);
  }, [records, filter, searchTerm]);

  const clearRecords = () => {
    if (window.confirm(t('records.confirmClear'))) {
      localStorage.removeItem(STORAGE_KEYS.SUCCESS_RECORDS);
      setRecords([]);
      setFilteredRecords([]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold leading-6 text-gray-900 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                {t('records.title')}
              </h3>
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                onClick={onClose}
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mt-4 flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{t('records.tableHeader.type')}:</span>
                <select 
                  value={filter} 
                  onChange={(e) => setFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  <option value="all">{t('records.filterAll')}</option>
                  <option value="single">{t('records.filterSingle')}</option>
                  <option value="batch">{t('records.filterBatch')}</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm text-gray-600">{t('records.search').split('...')[0]}:</span>
                <input
                  type="text"
                  placeholder={t('records.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm flex-1"
                />
              </div>
              
              <Button
                variant="danger"
                size="sm"
                onClick={clearRecords}
              >
                {t('records.clearAll')}
              </Button>
            </div>
          </div>

          <div className="bg-gray-50 px-6 py-4">
            <div className="text-sm text-gray-600 mb-2">
              共 {filteredRecords.length} 条记录 (总计 {records.length} 条)
            </div>
            
            <div className="max-h-96 overflow-y-auto bg-white rounded border">
              {filteredRecords.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {t('records.empty')}
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('records.tableHeader.type')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('records.tableHeader.email')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('records.tableHeader.code')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('records.tableHeader.time')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRecords.map((record, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            record.type === 'single' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {record.type === 'single' ? t('records.typeSingle') : t('records.typeBatch')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 max-w-[200px] truncate" title={record.email}>
                          {record.email}
                        </td>
                        <td className="px-3 py-2 text-sm font-mono text-gray-900 max-w-[150px] truncate" title={record.code}>
                          {record.code}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {formatTime(record.time)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="bg-gray-50 px-6 py-3 flex justify-end">
            <Button onClick={onClose}>
              {t('records.close')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};